import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router";
import { useSearchParams } from "react-router-dom";

import { 
  Table, 
  Button, 
  Input, 
  Modal, 
  message, 
  Tag, 
  Dropdown, 
  Menu, 
  Tooltip,
  Select
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  MoreOutlined,
  SearchOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import { RxLayers } from "react-icons/rx";
import PageHeader from "../../components/pageheader/PageHeader";
import EmptyState from "../../components/emptystate/EmptyState";

import {
  deleteArticleGroup,
  getArticleGroups,
} from "../../api/Services";
import { hasPermission, privilegesEnum } from "../../helpers/PermissionHelper";
import {
  useDebouncedValue,
  useFetchRequestGuard,
} from "../../helpers/FilterSearchHelper";

import styles from "./ArticleGroups.module.scss";

const ArticleGroups = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const getBasePath = () => {
    if (location.pathname.startsWith("/commercial-intelligence")) {
      return "/commercial-intelligence/configuration/article-groups";
    }
    if (location.pathname.startsWith("/promotions")) {
      return "/promotions/articlegroups";
    }
    return "/pricings/articlegroups";
  };

  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalSize, setTotalSize] = useState(0);
  const [actualPage, setActualPage] = useState(1);
  const { beginRequest, isStaleRequest } = useFetchRequestGuard();
  const [nameFilterValue, setNameFilterValue] = useState(searchParams.get("name") || "");
  const debouncedNameFilterValue = useDebouncedValue(nameFilterValue);
  const [descriptionFilterValue, setDescriptionFilterValue] = useState(searchParams.get("description") || "");
  const debouncedDescriptionFilterValue = useDebouncedValue(descriptionFilterValue);
  const [statusFilterValue, setStatusFilterValue] = useState<string | undefined>(searchParams.get("status") || undefined);
  const [originFilterValue, setOriginFilterValue] = useState<string | undefined>(searchParams.get("origin") || undefined);
  const [analysisFilterValue, setAnalysisFilterValue] = useState(searchParams.get("analysis") || "");
  const debouncedAnalysisFilterValue = useDebouncedValue(analysisFilterValue);

  const handleFetchArticleGroups = useCallback(
    async (pagination = actualPage) => {
      const requestId = beginRequest();
      setLoading(true);
      const response = await getArticleGroups(pagination, {
        name: debouncedNameFilterValue,
      });

      let filteredData = response.articleGroups;
      
      if (debouncedDescriptionFilterValue) {
        filteredData = filteredData.filter((item: any) => 
          item.description?.toLowerCase().includes(debouncedDescriptionFilterValue.toLowerCase())
        );
      }
      
      if (statusFilterValue !== undefined) {
        const isActive = statusFilterValue === "true";
        filteredData = filteredData.filter((item: any) => item.state === isActive);
      }
      
      if (originFilterValue) {
        filteredData = filteredData.filter((item: any) => 
          item.originInfo?.description_es?.toLowerCase().includes(originFilterValue.toLowerCase())
        );
      }
      
      if (debouncedAnalysisFilterValue) {
        filteredData = filteredData.filter((item: any) => 
          item.analysis?.toLowerCase().includes(debouncedAnalysisFilterValue.toLowerCase())
        );
      }

      if (isStaleRequest(requestId)) return;

      setData(filteredData);
      setTotalSize(filteredData.length);
      setLoading(false);
    },
    [actualPage, debouncedNameFilterValue, debouncedDescriptionFilterValue, statusFilterValue, originFilterValue, debouncedAnalysisFilterValue, beginRequest, isStaleRequest]
  );

  useEffect(() => {
    if (hasPermission(privilegesEnum.READ_ARTICLE_GROUP)) {
      const pageFromUrl = searchParams.get("page");
      if (pageFromUrl) {
        setActualPage(parseInt(pageFromUrl));
      }
      handleFetchArticleGroups();
    }
  }, [handleFetchArticleGroups, searchParams]);

  const handleDelete = async (id: number) => {
    setLoading(true);
    const deleteStatus = await deleteArticleGroup(id);
    if (!deleteStatus) message.error(t("Common-DeleteErrorMessage"));
    else message.success(t("Common-DeleteSuccessMessage"));
    await handleFetchArticleGroups();
    setLoading(false);
  };

  const updateURLParams = (filters: any) => {
    const params = new URLSearchParams();
    if (filters.name) params.set("name", filters.name);
    if (filters.description) params.set("description", filters.description);
    if (filters.status) params.set("status", filters.status);
    if (filters.origin) params.set("origin", filters.origin);
    if (filters.analysis) params.set("analysis", filters.analysis);
    if (filters.page && filters.page > 1) params.set("page", filters.page.toString());
    
    setSearchParams(params);
  };

  const handlePagination = (page: number) => {
    setActualPage(page);
    updateURLParams({
      name: nameFilterValue,
      description: descriptionFilterValue,
      status: statusFilterValue,
      origin: originFilterValue,
      analysis: analysisFilterValue,
      page: page
    });
    handleFetchArticleGroups(page);
  };

  const handleClearFilters = () => {
    setNameFilterValue("");
    setDescriptionFilterValue("");
    setStatusFilterValue(undefined);
    setOriginFilterValue(undefined);
    setAnalysisFilterValue("");
    setActualPage(1);
    setSearchParams(new URLSearchParams());
    handleFetchArticleGroups(1);
  };

  const hasActiveFilters = () => {
    return (
      nameFilterValue !== "" ||
      descriptionFilterValue !== "" ||
      statusFilterValue !== undefined ||
      originFilterValue !== undefined ||
      analysisFilterValue !== ""
    );
  };

  const hasNoData = () => {
    return !loading && data.length === 0;
  };

  const hasNoDataDueToFilters = () => {
    return hasNoData() && hasActiveFilters();
  };

  const hasNoDataAtAll = () => {
    return hasNoData() && !hasActiveFilters();
  };

  const getEmptyState = () => {
    if (hasNoDataDueToFilters()) {
      return <EmptyState type="noResults" entityName="ArticleGroup" />;
    }

    if (hasNoDataAtAll()) {
      return (
        <EmptyState
          type="noData"
          entityName="ArticleGroup"
          createPath={`${getBasePath()}/create`}
          createPermission={privilegesEnum.WRITE_ARTICLE_GROUP}
        />
      );
    }

    return null;
  };

  const showDeleteConfirm = (id: number) => {
    Modal.confirm({
      title: t("Common-DeleteConfirm"),
      icon: <ExclamationCircleOutlined />,
      content: "",
      okText: t("Common-Yes"),
      okType: "danger",
      cancelText: t("Common-No"),
      onOk() {
        handleDelete(id);
      },
    });
  };

  const columns = [
    {
      title: t("Common-Name"),
      dataIndex: "name",
      render: (name: string, record: any) => (
        <a
          href={`${getBasePath()}/${record.id}/view?${searchParams.toString()}`}
          className={styles.nameLink}
        >
          {name}
        </a>
      ),
    },
    {
      title: t("Common-Description"),
      dataIndex: "description",
    },
    {
      title: t("Common-Status"),
      dataIndex: "state",
      render: (enable: boolean) => (
        <Tag color={enable ? "green" : "red"}>
          {enable ? t("Common-Active") : t("Common-Inactive")}
        </Tag>
      ),
    },
    {
      title: t("Common-Origin"),
      key: "origin",
      render: (record: any) => record.originInfo?.description_es,
    },
    {
      title: t("Common-Analysis"),
      dataIndex: "analysis",
    },
    {
      title: t("Common-CreatedDate"),
      dataIndex: "createdAt",
      render: (date: string) => date?.split("T")[0],
    },
    {
      title: t("Common-Actions"),
      key: "action",
      render: (text: any, record: any) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item
                key="view"
                icon={<EyeOutlined />}
                onClick={() =>
                  navigate(`${getBasePath()}/${record.id}/view?${searchParams.toString()}`)
                }
              >
                {t("Common-View")}
              </Menu.Item>
              {hasPermission(privilegesEnum.WRITE_ARTICLE_GROUP) && (
                <Menu.Item
                  key="edit"
                  icon={<EditOutlined />}
                  onClick={() =>
                    navigate(`${getBasePath()}/${record.id}/view?${searchParams.toString()}`)
                  }
                >
                  {t("Common-Edit")}
                </Menu.Item>
              )}
              {hasPermission(privilegesEnum.DELETE_ARTICLE_GROUP) && (
                <Menu.Item
                  key="delete"
                  icon={<DeleteOutlined />}
                  onClick={() => showDeleteConfirm(record.id)}
                >
                  {t("Common-Delete")}
                </Menu.Item>
              )}
            </Menu>
          }
        >
          <Button className={styles.actionButtonMore}>
            <MoreOutlined className={styles.actionIconMore} />
          </Button>
        </Dropdown>
      ),
    },
  ];
  

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title={t("Common-ArticleGroups")}
        icon={<RxLayers />}
        onCreateClick={() => navigate(`${getBasePath()}/create`)}
        createPermission={privilegesEnum.WRITE_ARTICLE_GROUP}
      />
      <div className={styles.contentWrapper}>
        <div className={styles.tableWrapper}>
          <div className={styles.headerTableWrapper}>
            <div className={styles.filtersWrapper}>
              <div className={styles.filterWrapper}>
                <Input
                  placeholder={t("Common-SearchByName")}
                  className={styles.filterInput}
                  prefix={<SearchOutlined />}
                  allowClear
                  onChange={(e) => {
                    setNameFilterValue(e.target.value);
                    setActualPage(1);
                    updateURLParams({
                      name: e.target.value,
                      description: descriptionFilterValue,
                      status: statusFilterValue,
                      origin: originFilterValue,
                      analysis: analysisFilterValue,
                      page: 1
                    });
                  }}
                  value={nameFilterValue}
                />
              </div>
              <div className={styles.filterWrapper}>
                <Input
                  placeholder={t("Common-Description")}
                  className={styles.filterInput}
                  prefix={<SearchOutlined />}
                  allowClear
                  onChange={(e) => {
                    setDescriptionFilterValue(e.target.value);
                    setActualPage(1);
                    updateURLParams({
                      name: nameFilterValue,
                      description: e.target.value,
                      status: statusFilterValue,
                      origin: originFilterValue,
                      analysis: analysisFilterValue,
                      page: 1
                    });
                  }}
                  value={descriptionFilterValue}
                />
              </div>
              <div className={styles.filterWrapper}>
                <Select
                  showSearch
                  allowClear
                  value={statusFilterValue}
                  className={styles.filterSelect}
                  placeholder={t("Common-Status")}
                  filterOption={(input: string, option: any) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  onSelect={(_: any, option: any) => {
                    if (option) {
                      setStatusFilterValue(option.value);
                      setActualPage(1);
                      updateURLParams({
                        name: nameFilterValue,
                        description: descriptionFilterValue,
                        status: option.value,
                        origin: originFilterValue,
                        analysis: analysisFilterValue,
                        page: 1
                      });
                    }
                  }}
                  onClear={() => {
                    setStatusFilterValue(undefined);
                    setActualPage(1);
                    updateURLParams({
                      name: nameFilterValue,
                      description: descriptionFilterValue,
                      status: undefined,
                      origin: originFilterValue,
                      analysis: analysisFilterValue,
                      page: 1
                    });
                  }}
                >
                  <Select.Option value="false">
                    {t("Common-Inactive")}
                  </Select.Option>
                  <Select.Option value="true">{t("Common-Active")}</Select.Option>
                </Select>
              </div>
              <div className={styles.filterWrapper}>
                <Input
                  placeholder={t("Common-Origin")}
                  className={styles.filterInput}
                  prefix={<SearchOutlined />}
                  allowClear
                  onChange={(e) => {
                    setOriginFilterValue(e.target.value);
                    setActualPage(1);
                    updateURLParams({
                      name: nameFilterValue,
                      description: descriptionFilterValue,
                      status: statusFilterValue,
                      origin: e.target.value,
                      analysis: analysisFilterValue,
                      page: 1
                    });
                  }}
                  value={originFilterValue}
                />
              </div>
              <div className={styles.filterWrapper}>
                <Input
                  placeholder={t("Common-Analysis")}
                  className={styles.filterInput}
                  prefix={<SearchOutlined />}
                  allowClear
                  onChange={(e) => {
                    setAnalysisFilterValue(e.target.value);
                    setActualPage(1);
                    updateURLParams({
                      name: nameFilterValue,
                      description: descriptionFilterValue,
                      status: statusFilterValue,
                      origin: originFilterValue,
                      analysis: e.target.value,
                      page: 1
                    });
                  }}
                  value={analysisFilterValue}
                />
              </div>
              {hasActiveFilters() && (
                <div className={styles.filterWrapper}>
                  <Tooltip title={t("Common-Clear")}>
                    <Button
                      onClick={handleClearFilters}
                      className={styles.clearButton}
                      icon={<ClearOutlined />}
                    >
                      {t("Common-Clear")}
                    </Button>
                  </Tooltip>
                </div>
              )}
            </div>
          </div>

          <Table
            size="small"
            className={styles.table}
            loading={loading}
            columns={columns}
            dataSource={data}
            rowKey={(record) => record.id}
            pagination={{
              total: totalSize,
              showSizeChanger: false,
              onChange: handlePagination,
              current: actualPage,
              showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}`,
            }}
            locale={{
              emptyText: getEmptyState(),
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ArticleGroups;
